import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { invalidateAdminCache } from '../services/adminVerification';
import { logAdminStatus } from '../utils/adminDebug';
import { withTimeout, isRetryableError, delay } from '../utils/supabaseTimeout';

/**
 * Authentication Context
 * Manages user authentication state across the application using Supabase Auth
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use ref to track profile subscription (fixes closure issues)
  const profileSubscriptionRef = useRef(null);
  const periodicRefreshIntervalRef = useRef(null);

  // Fetch user profile from user_profiles table with timeout and retry logic
  const fetchProfile = async (userId, retryCount = 0) => {
    if (!isSupabaseConfigured || !userId) {
      setProfile(null);
      return;
    }

    const MAX_RETRIES = 2;
    const TIMEOUT_MS = 10000; // 10 seconds

    try {
      // Create the query promise
      const queryPromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // Wrap with timeout
      const { data, error } = await withTimeout(
        queryPromise,
        TIMEOUT_MS,
        'fetchProfile'
      );

      if (error) {
        console.error('[AuthContext] Error fetching profile:', {
          error: error.message || error,
          code: error.code,
          details: error.details,
          hint: error.hint,
          userId,
          retryCount,
          stack: error.stack,
        });

        // Retry on retryable errors
        if (retryCount < MAX_RETRIES && isRetryableError(error)) {
          const backoffDelay = 1000 * (retryCount + 1); // 1s, 2s
          console.log(`[AuthContext] Retrying profile fetch (attempt ${retryCount + 1}/${MAX_RETRIES}) after ${backoffDelay}ms...`);
          await delay(backoffDelay);
          return fetchProfile(userId, retryCount + 1);
        }

        throw error;
      }
      
      // Log admin status change for debugging
      const wasAdmin = profile?.role === 'admin';
      const isNowAdmin = data?.role === 'admin';
      if (wasAdmin !== isNowAdmin || import.meta.env.DEV) {
        logAdminStatus(userId, isNowAdmin, 'profile_fetch');
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Profile loaded:', {
            userId,
            role: data?.role,
            email: data?.email,
            fullName: data?.full_name,
          });
        }
      }
      
      setProfile(data);
      
      // Invalidate admin cache when profile changes
      invalidateAdminCache(userId);
    } catch (error) {
      console.error('[AuthContext] Error fetching profile (final):', {
        error: error.message || error,
        code: error.code,
        details: error.details,
        hint: error.hint,
        userId,
        retryCount,
        stack: error.stack,
      });
      
      // Don't clear profile on error if we already have one (preserve last known good state)
      // Only set to null if this is the initial fetch and we have no profile
      if (!profile) {
        setProfile(null);
      } else {
        console.warn('[AuthContext] Profile fetch failed but keeping existing profile data');
      }
      
      // Log critical errors prominently
      if (error.message?.includes('timeout') || error.code === '42P17') {
        console.error('[AuthContext] ⚠️ CRITICAL: Profile fetch failed with timeout or RLS error');
        console.error('[AuthContext] This may indicate an RLS policy issue or network problem');
        console.error('[AuthContext] Error details:', {
          message: error.message,
          code: error.code,
          userId,
        });
      }
    }
  };

  /**
   * Set up real-time subscription for profile changes
   */
  const setupProfileSubscription = (userId) => {
    // Clean up existing subscription
    if (profileSubscriptionRef.current) {
      supabase.removeChannel(profileSubscriptionRef.current);
      profileSubscriptionRef.current = null;
    }

    if (!isSupabaseConfigured || !userId) {
      return;
    }

    try {
      profileSubscriptionRef.current = supabase
        .channel(`user_profile_${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: `id=eq.${userId}`,
          },
          (payload) => {
            console.log('[AuthContext] Profile updated in real-time:', payload);
            
            // Log admin status change
            const wasAdmin = profile?.role === 'admin';
            const newRole = payload.new?.role;
            const isNowAdmin = newRole === 'admin';
            if (wasAdmin !== isNowAdmin) {
              logAdminStatus(userId, isNowAdmin, 'realtime_update');
            }
            
            // Update profile
            if (payload.new) {
              setProfile(payload.new);
              // Invalidate admin cache
              invalidateAdminCache(userId);
            } else {
              fetchProfile(userId);
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('[AuthContext] Profile subscription active for user:', userId);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('[AuthContext] Profile subscription error:', {
              status,
              userId,
            });
          } else {
            console.log('[AuthContext] Profile subscription status:', {
              status,
              userId,
            });
          }
        });
    } catch (error) {
      console.error('[AuthContext] Error setting up profile subscription:', error);
    }
  };

  /**
   * Set up periodic refresh (every 2 minutes)
   */
  const setupPeriodicRefresh = (userId) => {
    // Clear existing interval
    if (periodicRefreshIntervalRef.current) {
      clearInterval(periodicRefreshIntervalRef.current);
      periodicRefreshIntervalRef.current = null;
    }

    if (!userId) {
      return;
    }

    // Refresh profile every 2 minutes
    periodicRefreshIntervalRef.current = setInterval(() => {
      console.log('[AuthContext] Periodic profile refresh');
      fetchProfile(userId);
    }, 2 * 60 * 1000); // 2 minutes
  };

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Check if Supabase is configured
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      // Supabase not configured - skip auth initialization
      setIsLoading(false);
      return;
    }

    // Get initial session with timeout
    const SESSION_TIMEOUT_MS = 10000; // 10 seconds
    
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Session fetch timeout')), SESSION_TIMEOUT_MS);
    });

    Promise.race([sessionPromise, timeoutPromise])
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('[AuthContext] Error getting session:', {
            error: error.message || error,
            code: error.code,
            details: error.details,
            hint: error.hint,
          });
          setIsLoading(false);
          return;
        }
        
        if (session?.user) {
          console.log('[AuthContext] Session found, loading user profile...');
          setUser(session.user);
          // Use catch to prevent unhandled promise rejection
          fetchProfile(session.user.id).catch((err) => {
            console.error('[AuthContext] Failed to fetch profile on init:', {
              error: err.message || err,
              code: err.code,
              userId: session.user.id,
            });
          });
          setupProfileSubscription(session.user.id);
          setupPeriodicRefresh(session.user.id);
        } else {
          console.log('[AuthContext] No active session');
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('[AuthContext] Error initializing auth:', {
          error: error.message || error,
          type: error.name,
          code: error.code,
          stack: error.stack,
        });
        setIsLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        console.log('[AuthContext] Auth state changed:', event);
        
        // Clean up existing subscriptions
        if (profileSubscriptionRef.current) {
          supabase.removeChannel(profileSubscriptionRef.current);
          profileSubscriptionRef.current = null;
        }
        if (periodicRefreshIntervalRef.current) {
          clearInterval(periodicRefreshIntervalRef.current);
          periodicRefreshIntervalRef.current = null;
        }
        
        if (session?.user) {
          console.log('[AuthContext] User authenticated, fetching profile...');
          setUser(session.user);
          // Use catch to prevent unhandled promise rejection
          fetchProfile(session.user.id).catch((err) => {
            console.error('[AuthContext] Failed to fetch profile after auth change:', {
              error: err.message || err,
              code: err.code,
              details: err.details,
              hint: err.hint,
              userId: session.user.id,
              event,
              stack: err.stack,
            });
          });
          setupProfileSubscription(session.user.id);
          setupPeriodicRefresh(session.user.id);
        } else {
          console.log('[AuthContext] User signed out');
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        console.error('[AuthContext] Error in auth state change:', {
          error: error.message || error,
          code: error.code,
          event,
          stack: error.stack,
        });
      } finally {
        setIsLoading(false);
      }
    });

    // Cleanup function
    return () => {
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      if (profileSubscriptionRef.current) {
        supabase.removeChannel(profileSubscriptionRef.current);
        profileSubscriptionRef.current = null;
      }
      if (periodicRefreshIntervalRef.current) {
        clearInterval(periodicRefreshIntervalRef.current);
        periodicRefreshIntervalRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Sign in with email and password with timeout and profile fetch
  const signIn = async (email, password) => {
    if (!isSupabaseConfigured) {
      return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.' };
    }

    const TIMEOUT_MS = 15000; // 15 seconds for auth

    try {
      // Create auth promise
      const authPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });

      // Wrap with timeout
      const { data, error } = await withTimeout(
        authPromise,
        TIMEOUT_MS,
        'signIn'
      );

      if (error) {
        console.error('[AuthContext] Sign-in error:', {
          error: error.message || error,
          status: error.status,
          code: error.code,
          email,
        });
        throw error;
      }

      // After successful sign-in, fetch profile with retry logic
      if (data?.user) {
        console.log('[AuthContext] Sign-in successful, fetching profile...');
        try {
          await fetchProfile(data.user.id);
          console.log('[AuthContext] Profile loaded after sign-in');
        } catch (profileError) {
          // Log warning but don't block sign-in (user is authenticated, profile can load later)
          console.warn('[AuthContext] ⚠️ Profile fetch failed after sign-in, but user is authenticated:', {
            error: profileError.message || profileError,
            userId: data.user.id,
            email,
          });
        }
      }

      return { success: true, data };
    } catch (error) {
      console.error('[AuthContext] Sign-in failed:', {
        error: error.message || error,
        code: error.code,
        status: error.status,
        email,
        stack: error.stack,
      });
      return { 
        success: false, 
        error: error.message || 'Sign-in failed. Please check your credentials and try again.' 
      };
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, metadata = {}) => {
    if (!isSupabaseConfigured) {
      return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.' };
    }

    const TIMEOUT_MS = 15000; // 15 seconds

    try {
      const authPromise = supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata, // full_name, phone, etc.
        },
      });

      const { data, error } = await withTimeout(
        authPromise,
        TIMEOUT_MS,
        'signUp'
      );

      if (error) {
        console.error('[AuthContext] Sign-up error:', {
          error: error.message || error,
          status: error.status,
          code: error.code,
          email,
        });
        throw error;
      }
      return { success: true, data };
    } catch (error) {
      console.error('[AuthContext] Sign-up failed:', {
        error: error.message || error,
        code: error.code,
        email,
      });
      return { success: false, error: error.message || 'Sign-up failed. Please try again.' };
    }
  };

  // Sign out
  const signOut = async () => {
    if (!isSupabaseConfigured) {
      setUser(null);
      setProfile(null);
      return { success: true };
    }

    try {
      const TIMEOUT_MS = 5000; // 5 seconds for sign out
      const signOutPromise = supabase.auth.signOut();
      const { error } = await withTimeout(signOutPromise, TIMEOUT_MS, 'signOut');
      
      if (error) {
        console.error('[AuthContext] Sign-out error:', {
          error: error.message || error,
          code: error.code,
        });
        throw error;
      }
      setUser(null);
      setProfile(null);
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Sign-out failed:', {
        error: error.message || error,
        code: error.code,
      });
      // Still clear local state even if sign-out fails
      setUser(null);
      setProfile(null);
      return { success: false, error: error.message || 'Sign-out failed.' };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    if (!isSupabaseConfigured) {
      return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.' };
    }

    const TIMEOUT_MS = 10000; // 10 seconds

    try {
      const resetPromise = supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      const { error } = await withTimeout(resetPromise, TIMEOUT_MS, 'resetPassword');

      if (error) {
        console.error('[AuthContext] Reset password error:', {
          error: error.message || error,
          code: error.code,
          email,
        });
        throw error;
      }
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Reset password failed:', {
        error: error.message || error,
        code: error.code,
        email,
      });
      return { success: false, error: error.message || 'Failed to send reset email. Please try again.' };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    if (!isSupabaseConfigured) {
      return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.' };
    }

    const TIMEOUT_MS = 10000; // 10 seconds

    try {
      const updatePromise = supabase.auth.updateUser({
        password: newPassword,
      });

      const { error } = await withTimeout(updatePromise, TIMEOUT_MS, 'updatePassword');

      if (error) {
        console.error('[AuthContext] Update password error:', {
          error: error.message || error,
          code: error.code,
          userId: user?.id,
        });
        throw error;
      }
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Update password failed:', {
        error: error.message || error,
        code: error.code,
        userId: user?.id,
      });
      return { success: false, error: error.message || 'Failed to update password. Please try again.' };
    }
  };

  // Refresh profile
  const refreshProfile = async () => {
    if (user) {
      // Invalidate admin cache before refreshing
      invalidateAdminCache(user.id);
      await fetchProfile(user.id);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    profile,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    isAuthenticated,
    isAdmin: profile?.role === 'admin', // Computed property for admin check
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
